import { type Language, LanguageSchema } from '@shefira/shared';

/**
 * The language the interface speaks (ADR 0069). Chosen once per browser from
 * `navigator.language` (French if it starts with `fr`, English otherwise),
 * then by the player on the title screen or in the settings, and saved on
 * the account by `settings/sync.ts`. Windows are built once, so a change
 * reloads the page rather than repainting every string.
 */

const STORAGE_KEY = 'shefira.language';

/**
 * Whether English is offered to everyone (ADR 0069): the browser's language
 * decides at first visit and the switch shows on the welcome screen and in
 * the settings. Open since 0.24.0; `VITE_ENGLISH_OPEN=false` at build time
 * closes it again (the game then stays French, `?lang=en` still tries it).
 */
export const ENGLISH_OPEN = import.meta.env.VITE_ENGLISH_OPEN !== 'false';

function detect(): Language {
  // Tests read the French source, whatever language the machine speaks.
  if (import.meta.env.MODE === 'test') return 'fr';
  try {
    const params = new URLSearchParams(window.location.search);
    const asked = LanguageSchema.safeParse(params.get('lang'));
    if (asked.success) localStorage.setItem(STORAGE_KEY, asked.data);
    // Read once and wiped: a reload after the switch must not bring it back.
    if (params.has('lang')) {
      params.delete('lang');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      );
    }
    const saved = LanguageSchema.safeParse(localStorage.getItem(STORAGE_KEY));
    if (saved.success) return saved.data;
  } catch {
    /* storage blocked: the browser's language decides */
  }
  if (!ENGLISH_OPEN) return 'fr';
  const wanted = typeof navigator === 'undefined' ? 'fr' : navigator.language.toLowerCase();
  return wanted.startsWith('fr') ? 'fr' : 'en';
}

let current: Language = detect();
if (typeof document !== 'undefined') document.documentElement.lang = current;

export function language(): Language {
  return current;
}

type Saver = () => Promise<void>;
let saver: Saver | undefined;

/** How long a reload waits for the account to take the new language. */
const SAVE_WAIT_MS = 2000;

/**
 * Called by `settings/sync.ts` once a session exists: a language the player
 * picks is saved on the account before the page reloads, otherwise the
 * account's old language comes back with the reload and wins again.
 */
export function onLanguageChosen(save: Saver | undefined): void {
  saver = save;
}

/** Remember the language and reload into it; nothing happens when it is already the one spoken. */
export function setLanguage(next: Language, { reload = true, save = true } = {}): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* the language still holds for this visit */
  }
  if (!reload || typeof window === 'undefined') return;
  if (!save || saver === undefined) {
    window.location.reload();
    return;
  }
  const timeout = new Promise<void>((resolve) => window.setTimeout(resolve, SAVE_WAIT_MS));
  void Promise.race([saver().catch(() => undefined), timeout]).then(() => {
    window.location.reload();
  });
}
